import axios from "@/http/axios";

const getInvestmentProposalDefaultState = () => ({
  inputs: undefined,
  details: undefined,
  constituents: undefined,
  statistics: undefined,
  cumulativeReturns: undefined,
  lastPropertiesAllocation: undefined,
  lastHoldingAllocation: undefined,
  monthlyCumulativeReturns: undefined,
  yearlyCumulativeReturns: undefined,
  drawdowns: undefined,
  biggestDrawdowns: undefined,
  weightOptimization: undefined,
});

const state = getInvestmentProposalDefaultState();

const getters = {
  getInputs: (state) => {
    return state.inputs;
  },
  getDetails: (state) => {
    return state.details;
  },
  getConstituents: (state) => {
    return state.constituents;
  },
  getStatistics: (state) => {
    return state.statistics;
  },
  getCumulativeReturns: (state) => {
    return state.cumulativeReturns;
  },
  getLastPropertiesAllocation: (state) => {
    return state.lastPropertiesAllocation;
  },
  getLastHoldingAllocation: (state) => {
    return state.lastHoldingAllocation;
  },
  getMonthlyCumulativeReturns: (state) => {
    return state.monthlyCumulativeReturns;
  },
  getYearlyCumulativeReturns: (state) => {
    return state.yearlyCumulativeReturns;
  },
  getDrawdowns: (state) => {
    return state.drawdowns;
  },
  getBiggestDrawdowns: (state) => {
    return state.biggestDrawdowns;
  },
  getWeightOptimization: (state) => {
    return state.weightOptimization;
  },
};

const mutations = {
  SET_INPUTS: (state, inputs) => {
    state.inputs = inputs;
  },
  FETCH_DETAILS_SUCCESS: (state, { data }) => {
    state.details = data;
  },
  FETCH_CONSTITUENTS_SUCCESS: (state, { data }) => {
    state.constituents = data;
  },
  FETCH_STATISTICS_SUCCESS: (state, { data }) => {
    state.statistics = data;
  },
  FETCH_CUMULATIVE_RETURNS_SUCCESS: (state, { data }) => {
    state.cumulativeReturns = data;
  },
  FETCH_LAST_PROPERTIES_ALLOCATION_SUCCESS: (state, { data }) => {
    state.lastPropertiesAllocation = data;
  },
  FETCH_LAST_HOLDING_ALLOCATION_SUCCESS: (state, { data }) => {
    state.lastHoldingAllocation = data;
  },
  FETCH_MONTHLY_CUMULATIVE_RETURNS_SUCCESS: (state, { data }) => {
    state.monthlyCumulativeReturns = data;
  },
  FETCH_YEARLY_CUMULATIVE_RETURNS_SUCCESS: (state, { data }) => {
    state.yearlyCumulativeReturns = data;
  },
  FETCH_DRAWDOWNS_SUCCESS: (state, { data }) => {
    state.drawdowns = data;
  },
  FETCH_BIGGEST_DRAWDOWNS_SUCCESS: (state, { data }) => {
    state.biggestDrawdowns = data;
  },
  RESET_FETCHED_DATA(state) {
    // Reset most data fetched from the backend.
    state.statistics = undefined;
    state.cumulativeReturns = undefined;
    state.lastPropertiesAllocation = undefined;
    state.lastHoldingAllocation = undefined;
    state.monthlyCumulativeReturns = undefined;
    state.yearlyCumulativeReturns = undefined;
    state.drawdowns = undefined;
    state.biggestDrawdowns = undefined;
  },
  RESET_INPUTS_DATA(state) {
    state.inputs = undefined;
  },
  SET_WEIGHT_OPTIMIZATION(state, payload) {
    state.weightOptimization = payload;
  },
  RESET_WEIGHT_OPTIMIZATION(state) {
    state.weightOptimization = undefined;
  },
  RESET_ALL_STATES(state) {
    Object.assign(state, getInvestmentProposalDefaultState());
  },
};

const actions = {
  async fetchDetails({ commit }) {
    try {
      const response = await axios.get("/api/investment-proposal/details");
      commit("FETCH_DETAILS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_DETAILS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchConstituents({ commit }) {
    try {
      const response = await axios.get("/api/investment-proposal/constituents");
      commit("FETCH_CONSTITUENTS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_CONSTITUENTS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchStatistics({ commit }, { period, currency, composition }) {
    const data = {
      period,
      currency,
      composition,
    };
    try {
      const response = await axios.post("/api/investment-proposal/statistics", data);
      commit("FETCH_STATISTICS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_STATISTICS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchCumulativeReturns({ commit }, { period, currency, composition, startingValue }) {
    const data = {
      period,
      currency,
      composition,
      starting_value: startingValue,
    };
    try {
      const response = await axios.post("/api/investment-proposal/cumulative-returns", data);
      commit("FETCH_CUMULATIVE_RETURNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_CUMULATIVE_RETURNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchLastPropertiesAllocation({ commit }, { period, composition, lang }) {
    const data = {
      period,
      lang,
      composition,
    };
    try {
      const response = await axios.post(
        "/api/investment-proposal/last-properties-allocation",
        data
      );
      commit("FETCH_LAST_PROPERTIES_ALLOCATION_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_LAST_PROPERTIES_ALLOCATION_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchLastHoldingAllocation({ commit }, { period, composition, lang }) {
    const data = {
      period,
      lang,
      composition,
    };
    try {
      const response = await axios.post("/api/investment-proposal/last-holding-allocation", data);
      commit("FETCH_LAST_HOLDING_ALLOCATION_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_LAST_HOLDING_ALLOCATION_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchMonthlyCumulativeReturns({ commit }, { period, currency, composition }) {
    const data = {
      period,
      currency,
      composition,
    };
    try {
      const response = await axios.post(
        "/api/investment-proposal/monthly-cumulative-returns",
        data
      );
      commit("FETCH_MONTHLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_MONTHLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchYearlyCumulativeReturns({ commit }, { period, currency, composition }) {
    const data = {
      period,
      currency,
      composition,
    };
    try {
      const response = await axios.post("/api/investment-proposal/yearly-cumulative-returns", data);
      commit("FETCH_YEARLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_YEARLY_CUMULATIVE_RETURNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchDrawdowns({ commit }, { period, currency, composition }) {
    const data = {
      period,
      currency,
      composition,
    };
    try {
      const response = await axios.post("/api/investment-proposal/drawdowns", data);
      commit("FETCH_DRAWDOWNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_DRAWDOWNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  async fetchBiggestDrawdowns({ commit }, { period, currency, composition }) {
    const data = {
      period,
      currency,
      composition,
    };
    try {
      const response = await axios.post("/api/investment-proposal/biggest-drawdowns", data);
      commit("FETCH_BIGGEST_DRAWDOWNS_SUCCESS", {
        data: response.data,
      });
    } catch (error) {
      commit("FETCH_BIGGEST_DRAWDOWNS_SUCCESS", {
        data: null,
      });
      // We rethrow the error for sentry to catch it.
      throw error;
    }
  },
  resetAllStates({ commit }) {
    commit("RESET_ALL_STATES");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
